/**
 * Patients — patient-centric gap chase worklist (Tier A).
 *
 * Two lenses over the same GROUP-lane outcomes:
 *  - By patient: measure-gated list (left) + worksheet for selected measures (right).
 *    List does not load until the user picks at least one measure.
 *  - By measure: measure dropdown + full-width Gap Management Detail (embedded).
 *
 * Plan: canvases/patients-tab-plan.md
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAco } from "@/contexts/AcoContext";
import { useActiveProgram } from "@/platform/useActiveProgram";
import { usePerformanceSummaries, useAcoTinNpiPerformance } from "@/hooks/queries/useMipsQueries";
import { getProviders } from "@/lib/measure-outcomes";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useChartAbstraction } from "@/contexts/ChartAbstractionContext";
import { YearLockBanner } from "@/components/gap-management/shared/YearLockBanner";
import { apiClient } from "@/lib/api-client";
import { batchUpdatePatientOutcomes, checkJobStatus } from "@/lib/measure-outcomes";
import { qk, keepPreviousSameEntity } from "@/lib/query-keys";
import userStorage from "@/lib/userStorage";
import { MipsDataService } from "@/lib/mips-data-service";
import MeasureDetail from "@/pages/programs/mips/gap-management/quality/Detail";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  Download,
  ExternalLink,
  Printer,
  Search,
  Users,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ViewMode = "patient" | "measure";
/** open = still needs advantageous numerator (inverse-aware). met/not_met = recorded state. */
type GapStatus =
  | "open"
  | "incomplete"
  | "favorable"
  | "unfavorable"
  | "not_met"
  | "met"
  | "all";
type ChaseStatus = "not_complete" | "complete" | "all";
type MeasureStatus = "met" | "not_met" | "exception" | "excluded" | "incomplete";

interface PatientListItem {
  patientId: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  dob: string | null;
  gender: string | null;
  providers: string[];
  openGaps: number;
  measureCount: number;
  gapMeasureIds: string[];
  isComplete: boolean;
}

interface PatientListResponse {
  total: number;
  page: number;
  pageSize: number;
  patients: PatientListItem[];
}

interface WorksheetMeasure {
  measureId: string;
  submissionMethod: string;
  stratum: string | null;
  status: MeasureStatus;
  isInverse?: boolean;
  dos: string | null;
  cptCodes: string | null;
  icd10Codes: string | null;
  npi: string | null;
  npiName: string | null;
  encounterId: string | null;
  version: number;
  changeType: string | null;
}

interface PatientEncounter {
  dos: string | null;
  cptCodes: string | null;
  icd10Codes: string | null;
  cptModifiers: string | null;
  npi: string | null;
  placeOfService: string | null;
  encounterId: string | null;
}

interface PatientDetail {
  patientId: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  dob: string | null;
  gender: string | null;
  providers: string[];
  isComplete: boolean;
  measures: WorksheetMeasure[];
  encounters?: PatientEncounter[];
}

/** Clinical result filter (recorded outcome quality — inverse-aware). */
type ShowPreset = "no_result" | "good_result" | "bad_result" | "all";

interface PersistedFilters {
  view: ViewMode;
  measureIds: string[];
  /** Collection-type lane (MIPS CQM | eCQM | Medicare CQM). null = first available. */
  collectionType?: string | null;
  /** Participant TIN (APP Plus only). null = all TINs. */
  tin?: string | null;
  /** Clinician NPI (patients seen by this clinician). null = all. */
  npi?: string | null;
  /** Clinical result scope (maps to API status + chase). */
  show: ShowPreset;
  /** @deprecated kept for one-time migration from older localStorage */
  status?: GapStatus;
  chase?: ChaseStatus;
  /** Minimum open-gap count on the worklist (0 = any). */
  minGaps: number;
}

const SHOW_PRESETS: Record<
  ShowPreset,
  { label: string; tooltip: string; status: GapStatus; chase: ChaseStatus }
> = {
  no_result: {
    label: "No result yet",
    tooltip:
      "Selected measure(s) have no Met / Not Met / Exception recorded. Skipped patients are hidden.",
    status: "incomplete",
    chase: "not_complete",
  },
  good_result: {
    label: "Good result",
    tooltip:
      "Advantageous numerator recorded — Met for normal measures, Not Met for inverse. Skipped patients are hidden.",
    status: "favorable",
    chase: "not_complete",
  },
  bad_result: {
    label: "Bad result",
    tooltip:
      "Disadvantageous numerator recorded — Not Met for normal measures, Met for inverse. Skipped patients are hidden.",
    status: "unfavorable",
    chase: "not_complete",
  },
  all: {
    label: "All statuses",
    tooltip:
      "No clinical-result filter. Every patient matching measures, search, and open-gaps (includes skipped).",
    status: "all",
    chase: "all",
  },
};

function migrateShowPreset(stored: Partial<PersistedFilters> & { show?: string }): ShowPreset {
  const raw = stored.show;
  if (raw && raw in SHOW_PRESETS) return raw as ShowPreset;
  // Legacy presets → closest Clinical result option
  if (raw === "needs_work" || raw === "open") return "no_result";
  if (raw === "documented_met" || raw === "met") return "good_result";
  if (raw === "documented_not_met" || raw === "not_met") return "bad_result";
  if (raw === "finished" || raw === "complete") return "all";
  const status = stored.status ?? "incomplete";
  if (status === "incomplete" || status === "open") return "no_result";
  if (status === "met" || status === "favorable") return "good_result";
  if (status === "not_met" || status === "unfavorable") return "bad_result";
  return "no_result";
}

const DEFAULT_FILTERS: PersistedFilters = {
  view: "patient",
  measureIds: [],
  collectionType: null,
  tin: null,
  npi: null,
  show: "no_result",
  minGaps: 0,
};

function measureIsGoodResult(m: WorksheetMeasure): boolean {
  if (m.status === "exception" || m.status === "excluded" || m.status === "incomplete") {
    return false;
  }
  return m.isInverse ? m.status === "not_met" : m.status === "met";
}

function measureIsBadResult(m: WorksheetMeasure): boolean {
  if (m.status === "exception" || m.status === "excluded" || m.status === "incomplete") {
    return false;
  }
  return m.isInverse ? m.status === "met" : m.status === "not_met";
}

const PAGE_SIZE = 25;
/** Floor on open-gap count. 0 = no floor (show everyone matching Show). */
const MIN_GAPS_OPTIONS = [
  { value: 0, label: "All" },
  { value: 1, label: "1+" },
  { value: 2, label: "2+" },
  { value: 3, label: "3+" },
  { value: 4, label: "4+" },
] as const;

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<MeasureStatus, string> = {
  met: "Met",
  not_met: "Not Met",
  exception: "Exception",
  excluded: "Excluded",
  incomplete: "Needs review",
};

/** Colors reflect performance quality: green = advantageous. Inverse swaps Met/Not Met. */
function statusClassName(status: MeasureStatus, isInverse?: boolean): string {
  if (status === "met") {
    return isInverse
      ? "bg-red-100 text-red-800 border-red-200"
      : "bg-green-100 text-green-800 border-green-200";
  }
  if (status === "not_met") {
    return isInverse
      ? "bg-green-100 text-green-800 border-green-200"
      : "bg-red-100 text-red-800 border-red-200";
  }
  if (status === "exception") return "bg-amber-100 text-amber-800 border-amber-200";
  if (status === "excluded") return "bg-slate-100 text-slate-600 border-slate-200";
  return "bg-slate-100 text-slate-500 border-dashed border-slate-300";
}

function StatusBadge({ status, isInverse }: { status: MeasureStatus; isInverse?: boolean }) {
  return (
    <Badge variant="outline" className={`shrink-0 font-medium ${statusClassName(status, isInverse)}`}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
}

function splitCodes(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/[,;|\s]+/)
    .map((c) => c.trim())
    .filter(Boolean);
}

function CodeChips({
  codes,
  tone = "slate",
}: {
  codes: string[];
  tone?: "slate" | "blue" | "amber";
}) {
  if (codes.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  const toneCls =
    tone === "blue"
      ? "bg-blue-50 text-blue-800 ring-blue-100"
      : tone === "amber"
        ? "bg-amber-50 text-amber-900 ring-amber-100"
        : "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((c) => (
        <span
          key={c}
          className={`inline-flex rounded px-1.5 py-0.5 font-mono text-[11px] font-medium ring-1 ring-inset ${toneCls}`}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function csvEscape(value: string | number | boolean | null | undefined): string {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: Array<string | number | boolean | null | undefined>): string {
  return cells.map(csvEscape).join(",");
}

/**
 * One flat Excel-friendly table: patient columns + record type + measure/encounter fields.
 * UTF-8 BOM so Excel opens encoding correctly.
 */
function downloadPatientCsv(detail: PatientDetail, programYear: number) {
  const providers = detail.providers.join("; ");
  const base = [
    detail.patientId,
    detail.fullName,
    detail.dob ?? "",
    detail.gender ?? "",
    providers,
    detail.isComplete ? "Yes" : "No",
    programYear,
  ];

  const header = [
    "Patient ID",
    "Full Name",
    "DOB",
    "Gender",
    "Providers (NPI)",
    "Skipped",
    "Program Year",
    "Record Type",
    "Measure ID",
    "Submission Method",
    "Stratum",
    "Status",
    "Inverse",
    "Date of Service",
    "CPT",
    "Modifiers",
    "ICD-10",
    "NPI",
    "Place of Service",
    "Encounter ID",
  ];

  const lines: string[] = [csvRow(header)];

  for (const m of detail.measures) {
    lines.push(
      csvRow([
        ...base,
        "Measure",
        m.measureId,
        m.submissionMethod,
        m.stratum,
        STATUS_LABEL[m.status] ?? m.status,
        m.isInverse ? "Yes" : "No",
        m.dos,
        m.cptCodes,
        "",
        m.icd10Codes,
        m.npi,
        "",
        m.encounterId,
      ]),
    );
  }

  for (const enc of detail.encounters ?? []) {
    lines.push(
      csvRow([
        ...base,
        "Encounter",
        "",
        "",
        "",
        "",
        "",
        enc.dos,
        enc.cptCodes,
        enc.cptModifiers,
        enc.icd10Codes,
        enc.npi,
        enc.placeOfService,
        enc.encounterId,
      ]),
    );
  }

  // If somehow neither section has rows, still emit demographics.
  if (lines.length === 1) {
    lines.push(csvRow([...base, "Patient", "", "", "", "", "", "", "", "", "", "", "", ""]));
  }

  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = (detail.fullName || detail.patientId)
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 40);
  a.download = `${safeName}_${detail.patientId}_${programYear}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ListSkeleton() {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
      ))}
    </div>
  );
}

function QueryError({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <div className="space-y-3 p-6 text-center">
      <p className="text-sm font-medium text-destructive">Couldn't load patients.</p>
      {message && <p className="text-xs text-muted-foreground">{message}</p>}
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function WorklistPager({
  page,
  totalPages,
  onPageChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2"
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="min-w-[3.5rem] text-center text-xs font-medium text-slate-600">
        {page} / {totalPages}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2"
        disabled={disabled || page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question-driven worksheet cards
// ---------------------------------------------------------------------------

/**
 * Abstraction questions are keyed by the zero-padded BASE measure id ("001").
 * Worksheet rows can carry variants ("001SSP", "005_P") — extract the numeric
 * base so question lookup (and chart-abstraction adds) hit the right key.
 */
function baseMeasureId(measureId: string): string {
  const numeric = measureId.match(/^\d+/)?.[0];
  return numeric ? numeric.padStart(3, "0") : measureId;
}

type YesNo = "yes" | "no";
type RecordableOutcome = "met" | "not_met" | "exception";

/**
 * Value-entry prompts from the uploaded chart-abstraction document (A1C, BP,
 * etc.). Shown as guidance only — we do NOT invent value→outcome scoring.
 */
function isValuePrompt(q: string): boolean {
  return /a1c|hba1c|blood pressure|systolic|diastolic|please add|enter (the|a)|value \(can use|most recent blood/i.test(
    q,
  );
}

/**
 * Yes/No interview lines from the authored document. Some exception-branch
 * lines omit a trailing "?" (e.g. measure 047 Q2) — still treat as Yes/No when
 * they open like a clinical question.
 */
function isYesNoQuestion(q: string): boolean {
  if (isValuePrompt(q)) return false;
  if (q.includes("?")) return true;
  return /^(was|were|is|are|did|does|do|has|have|if)\b/i.test(q.trim());
}

/**
 * Convention for authored Yes/No interview sets only:
 *  - 1 Y/N: Yes → Met · No → Not Met
 *  - 2 Y/N: Q1 Yes → Met · Q1 No + Q2 Yes → Exception · Q1 No + Q2 No → Not Met
 * Value prompts never participate (no invented numeric thresholds).
 */
function deriveSuggestion(
  questions: string[],
  answers: Array<YesNo | null>,
): RecordableOutcome | null {
  const ynIdx = questions
    .map((q, i) => (isYesNoQuestion(q) ? i : -1))
    .filter((i) => i >= 0);
  if (ynIdx.length === 1) {
    if (answers[ynIdx[0]] === "yes") return "met";
    if (answers[ynIdx[0]] === "no") return "not_met";
    return null;
  }
  if (ynIdx.length === 2) {
    const a0 = answers[ynIdx[0]];
    const a1 = answers[ynIdx[1]];
    if (a0 === "yes") return "met";
    if (a0 === "no") {
      if (a1 === "yes") return "exception";
      if (a1 === "no") return "not_met";
    }
    return null;
  }
  return null;
}

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: YesNo | null;
  onChange: (v: YesNo | null) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <Button
        variant={value === "yes" ? "default" : "outline"}
        size="sm"
        className={`h-8 min-w-[4.5rem] px-4 ${
          value === "yes" ? "" : "border-slate-300 bg-white font-medium text-slate-800 hover:bg-slate-50"
        }`}
        disabled={disabled}
        onClick={() => onChange(value === "yes" ? null : "yes")}
      >
        Yes
      </Button>
      <Button
        variant={value === "no" ? "default" : "outline"}
        size="sm"
        className={`h-8 min-w-[4.5rem] px-4 ${
          value === "no" ? "" : "border-slate-300 bg-white font-medium text-slate-800 hover:bg-slate-50"
        }`}
        disabled={disabled}
        onClick={() => onChange(value === "no" ? null : "no")}
      >
        No
      </Button>
    </div>
  );
}

interface WorksheetMeasureCardProps {
  m: WorksheetMeasure;
  title: string;
  questions: string[];
  saving: boolean;
  locked: boolean;
  onRecord: (m: WorksheetMeasure, outcome: RecordableOutcome | null) => void;
}

/**
 * One measure on the worksheet. Authored Yes/No questions SUGGEST an outcome;
 * Met / Not Met / Exception remain the recording control (click again to clear,
 * same as Gap Management Detail). Value prompts are guidance only.
 */
function WorksheetMeasureCard({
  m,
  title,
  questions,
  saving,
  locked,
  onRecord,
}: WorksheetMeasureCardProps) {
  const [answers, setAnswers] = useState<Array<YesNo | null>>(
    () => questions.map(() => null),
  );

  const ynIndices = useMemo(
    () =>
      questions
        .map((q, i) => (isYesNoQuestion(q) ? i : -1))
        .filter((i) => i >= 0),
    [questions],
  );
  const suggestion = deriveSuggestion(questions, answers);
  const twoStepYn = ynIndices.length === 2;

  const answerQuestion = (idx: number, v: YesNo | null) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = v;
      // Re-answering / clearing the first Y/N step clears the exception branch.
      if (twoStepYn && idx === ynIndices[0]) {
        next[ynIndices[1]] = null;
      }
      return next;
    });
  };

  const clickOutcome = (outcome: RecordableOutcome) => {
    // Toggle off when re-clicking the active numerator (Detail parity).
    onRecord(m, m.status === outcome ? null : outcome);
  };

  // Able-style: soft card wash by clinical result (no accent borders).
  const advantageous =
    m.status === "exception" || m.status === "excluded"
      ? true
      : m.isInverse
        ? m.status === "not_met"
        : m.status === "met";
  const cardBg =
    m.status === "incomplete" || m.status === "excluded"
      ? "border-slate-200 bg-slate-50"
      : advantageous
        ? "border-emerald-200/80 bg-emerald-50/70"
        : "border-rose-200/80 bg-rose-50/70";

  return (
    <div className={`patients-print-card rounded-lg border p-4 shadow-sm ${cardBg}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {m.measureId}
            {title ? ` — ${title}` : ""}
            {m.stratum ? ` (${m.stratum})` : ""}
          </div>
          <div className="mt-0.5 text-xs text-slate-600">
            {m.dos ? `Date of Service ${formatDate(m.dos)}` : "No qualifying encounter"}
            {m.npiName ? ` · ${m.npiName}` : ""}
            {m.changeType === "manual_edit" || m.changeType === "batch_update"
              ? " · manually edited"
              : ""}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {(() => {
            const spec = MipsDataService.getInstance().getMeasureSpecificationUrl(
              m.measureId,
              m.submissionMethod,
            );
            return spec ? (
              <Button
                variant="outline"
                size="sm"
                className="patients-print-hide h-7"
                onClick={() => window.open(spec, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                Measure Spec
              </Button>
            ) : null;
          })()}
          {m.isInverse && (
            <Badge variant="outline" className="shrink-0 text-[10px] font-normal text-muted-foreground">
              Inverse
            </Badge>
          )}
          <StatusBadge status={m.status} isInverse={m.isInverse} />
        </div>
      </div>

      {/* Interview (screen) */}
      {questions.length > 0 && (
        <div className="patients-print-hide mt-3 space-y-2.5 rounded-md border border-white/70 bg-white/70 p-3">
          {questions.map((q, i) => {
            const yn = isYesNoQuestion(q);
            const valuePrompt = isValuePrompt(q);
            // 2-step Y/N: hide the exception-branch question until Q1 = No.
            if (twoStepYn && i === ynIndices[1] && answers[ynIndices[0]] !== "no") {
              return null;
            }
            return (
              <div key={i} className="space-y-1.5">
                <p className="text-xs leading-relaxed text-slate-700">
                  {questions.length > 1 ? `${i + 1}. ` : ""}
                  {q}
                </p>
                {yn ? (
                  <YesNoToggle
                    value={answers[i]}
                    onChange={(v) => answerQuestion(i, v)}
                    disabled={locked || saving}
                  />
                ) : valuePrompt ? (
                  <p className="text-[11px] text-muted-foreground">
                    Enter the value in the chart / notes, then record Met, Not Met, or
                    Exception below. (No automatic scoring from the value.)
                  </p>
                ) : null}
              </div>
            );
          })}
          {suggestion && suggestion !== m.status && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
              <span className="text-xs text-slate-700">
                Answers suggest:{" "}
                <span className="font-semibold">{STATUS_LABEL[suggestion]}</span>
              </span>
              <Button
                size="sm"
                className="h-8 px-3"
                disabled={locked || saving}
                onClick={() => onRecord(m, suggestion)}
              >
                {saving ? "Saving…" : `Record ${STATUS_LABEL[suggestion]}`}
              </Button>
            </div>
          )}
          {suggestion && suggestion === m.status && (
            <p className="text-xs text-muted-foreground">
              Answers match the recorded outcome ({STATUS_LABEL[suggestion]}).
            </p>
          )}
        </div>
      )}

      {/* Interview (print) */}
      {questions.length > 0 && (
        <div className="mt-2 hidden space-y-2 print:block">
          {questions.map((q, i) => (
            <div key={i} className="text-xs">
              <p className="leading-snug">
                {questions.length > 1 ? `${i + 1}. ` : ""}
                {q}
              </p>
              {isYesNoQuestion(q) ? (
                <span className="mt-0.5 flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <span className="patients-print-checkbox" /> Yes
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="patients-print-checkbox" /> No
                  </span>
                </span>
              ) : (
                <p className="mt-0.5">Value / notes: ____________________________</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 hidden gap-4 text-xs print:flex">
        <span className="flex items-center gap-1">
          <span className="patients-print-checkbox" /> Met
        </span>
        <span className="flex items-center gap-1">
          <span className="patients-print-checkbox" /> Not Met
        </span>
        <span className="flex items-center gap-1">
          <span className="patients-print-checkbox" /> Exception
        </span>
        <span className="ml-auto">Notes: ____________________________</span>
      </div>

      <div className="patients-print-hide mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant={
            m.status === "met"
              ? m.isInverse
                ? "destructive"
                : "default"
              : "outline"
          }
          size="sm"
          className={`h-9 min-w-[5.5rem] px-4 text-sm font-semibold ${
            m.status === "met"
              ? ""
              : "border-2 border-slate-400 bg-white text-slate-800 hover:border-slate-500 hover:bg-slate-50"
          }`}
          disabled={locked || saving}
          onClick={() => clickOutcome("met")}
        >
          Met
        </Button>
        <Button
          variant={
            m.status === "not_met"
              ? m.isInverse
                ? "default"
                : "destructive"
              : "outline"
          }
          size="sm"
          className={`h-9 min-w-[5.5rem] px-4 text-sm font-semibold ${
            m.status === "not_met"
              ? ""
              : "border-2 border-slate-400 bg-white text-slate-800 hover:border-slate-500 hover:bg-slate-50"
          }`}
          disabled={locked || saving}
          onClick={() => clickOutcome("not_met")}
        >
          Not Met
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 min-w-[5.5rem] px-4 text-sm font-semibold ${
            m.status === "exception"
              ? "border-amber-600 bg-amber-600 text-white hover:bg-amber-700 hover:text-white"
              : "border-2 border-slate-400 bg-white text-slate-800 hover:border-slate-500 hover:bg-slate-50"
          }`}
          disabled={locked || saving}
          onClick={() => clickOutcome("exception")}
        >
          Exception
        </Button>
        {saving && <span className="text-xs text-muted-foreground">Saving…</span>}
      </div>
    </div>
  );
}

/** Elegant multi-select for campaign measures (search + checkboxes). */
function MeasureMultiSelect({
  measureIds,
  selectedIds,
  measureTitle,
  onChange,
  disabled,
}: {
  measureIds: string[];
  selectedIds: string[];
  measureTitle: (id: string) => string;
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return measureIds;
    return measureIds.filter(
      (id) =>
        id.toLowerCase().includes(q) ||
        measureTitle(id).toLowerCase().includes(q),
    );
  }, [measureIds, measureTitle, query]);

  const allSelected = measureIds.length > 0 && selectedIds.length === measureIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const label =
    selectedIds.length === 0
      ? null
      : selectedIds.length === 1
        ? selectedIds[0]
        : `${selectedIds.length} measures`;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 min-w-[220px] justify-between px-3 font-normal"
        >
          {label ? (
            <span className="truncate">
              <span className="font-medium">{label}</span>
              {selectedIds.length === 1 && measureTitle(selectedIds[0]) ? (
                <span className="ml-1.5 text-muted-foreground">
                  — {measureTitle(selectedIds[0]).slice(0, 28)}
                  {measureTitle(selectedIds[0]).length > 28 ? "…" : ""}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Select measures…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="border-b p-2">
          <Input
            placeholder="Search measures…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="patients-measure-select-all"
              className="h-[18px] w-[18px] rounded-[3px]"
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={(v) => onChange(v ? [...measureIds] : [])}
            />
            <Label htmlFor="patients-measure-select-all" className="cursor-pointer text-sm font-medium">
              Select all
            </Label>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([])}
            disabled={selectedIds.length === 0}
          >
            Clear
          </Button>
        </div>
        <div className="max-h-72 space-y-0.5 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-sm text-muted-foreground">No matches.</p>
          )}
          {filtered.map((id) => {
            const checked = selectedIds.includes(id);
            return (
              <label
                key={id}
                className="flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 hover:bg-muted/60"
              >
                <Checkbox
                  checked={checked}
                  className="mt-0.5"
                  onCheckedChange={(v) =>
                    onChange(
                      v === true
                        ? [...selectedIds, id].sort()
                        : selectedIds.filter((m) => m !== id),
                    )
                  }
                />
                <span className="min-w-0">
                  <span className="block font-mono text-sm font-semibold">{id}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {measureTitle(id)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Patients() {
  const { selectedPractice } = useAuth();
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { getMeasureQuestions } = useChartAbstraction();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [exportingWorklist, setExportingWorklist] = useState(false);

  // APP Plus mode: the worklist targets the selected ACO's shadow practice
  // (same rule as Upload History / QPS Management) — the MIPS practice
  // selector is not shown in that program, so selectedPractice is unset.
  const activeProgram = useActiveProgram();
  const { acos, selectedAco } = useAco();
  const appMode = activeProgram === "app-plus" && acos.length > 0;
  const practiceId = appMode
    ? (selectedAco?.shadow_practice_id?.toString() ?? "")
    : (selectedPractice?.id?.toString() ?? "");

  // -------------------------------------------------------------------------
  // Filters (persisted per user, survives idle logout)
  // -------------------------------------------------------------------------
  const [filters, setFilters] = useState<PersistedFilters>(() => {
    try {
      const stored = JSON.parse(userStorage.getItem("patients:filters") || "null");
      if (!stored) return DEFAULT_FILTERS;
      const { status: _s, chase: _c, ...rest } = stored as PersistedFilters;
      return {
        ...DEFAULT_FILTERS,
        ...rest,
        show: migrateShowPreset(stored),
      };
    } catch {
      return DEFAULT_FILTERS;
    }
  });
  useEffect(() => {
    // Persist only the combined `show` (drop legacy status/chase keys).
    const { status: _s, chase: _c, ...rest } = filters;
    userStorage.setItem("patients:filters", JSON.stringify(rest));
  }, [filters]);

  const showScope = SHOW_PRESETS[filters.show] ?? SHOW_PRESETS.no_result;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [page, setPage] = useState(1);
  /** By-measure lens: `measureId:submissionMethod` (same key Detail uses). */
  const [focusMeasureKey, setFocusMeasureKey] = useState<string | null>(null);

  // PHI hygiene: patient selection lives in memory ONLY — never in the URL.
  // Patient IDs are MBIs (HIPAA identifiers); a ?patientId= query param lands
  // in browser history and stays visible on the logged-out login screen.
  // Old bookmarked links still honor the param once, then it is stripped.
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    () => searchParams.get("patientId"),
  );
  useEffect(() => {
    if (!searchParams.has("patientId")) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("patientId");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // APP Plus: the embedded Detail view's library fetches (getPatientRows etc.
  // in measure-outcomes.ts) resolve the practice from ?practiceId= or the MIPS
  // session — never from props. Pin the shadow practice on the URL while in
  // APP Plus (the D12 convention QPS "View Details" uses; a practice id is not
  // PHI) and strip it in MIPS mode so a stale value can't redirect fetches.
  useEffect(() => {
    const current = searchParams.get("practiceId");
    const wanted = appMode && practiceId ? practiceId : null;
    if (current === wanted) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (wanted) next.set("practiceId", wanted);
        else next.delete("practiceId");
        return next;
      },
      { replace: true },
    );
  }, [appMode, practiceId, searchParams, setSearchParams]);

  // Handoff prefilters (Insights → Patients): ?tin= ?npi= ?collectionType=.
  // TIN / NPI are provider identifiers (public NPPES data), not PHI. Applied
  // once, then stripped so they don't pin the filters on later navigation.
  useEffect(() => {
    const tin = searchParams.get("tin");
    const npi = searchParams.get("npi");
    const ct = searchParams.get("collectionType");
    if (!tin && !npi && !ct) return;
    setFilters((f) => ({
      ...f,
      view: "patient",
      ...(ct ? { collectionType: ct } : {}),
      ...(tin ? { tin } : {}),
      ...(npi ? { npi } : {}),
    }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tin");
        next.delete("npi");
        next.delete("collectionType");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Reset paging when scope changes
  useEffect(() => {
    setPage(1);
  }, [
    filters.measureIds.join(","),
    filters.show,
    filters.minGaps,
    filters.tin,
    filters.npi,
    search,
    practiceId,
    programYear,
    filters.view,
    focusMeasureKey,
  ]);

  // -------------------------------------------------------------------------
  // Measure titles (year-keyed static data — read reactively on programYear)
  // -------------------------------------------------------------------------
  const dataService = useMemo(() => {
    const ds = MipsDataService.getInstance();
    ds.setActiveYear(programYear);
    return ds;
  }, [programYear]);

  const measureTitle = useCallback(
    (measureId: string) => dataService.getCmsMeasure(measureId)?.title ?? "",
    [dataService],
  );

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  // Practice's selected quality measures (for the measure filter + by-measure lens)
  const selectionsQuery = useQuery({
    queryKey: qk.practiceMeasureSelections(practiceId, programYear),
    queryFn: () =>
      apiClient.get(
        `/api/practices/${practiceId}/measures?category=quality&active_only=true&year=${programYear}`,
      ) as Promise<Array<{ measure_id: string; submission_method: string }>>,
    enabled: !!practiceId && !appMode,
    placeholderData: keepPreviousSameEntity(2, practiceId),
  });

  // APP Plus: the shadow practice has no practice_measure_selections — its
  // measure set is whatever the pipeline calculated (SSP twins under
  // Medicare CQM, or base ids under MIPS CQM / eCQM). Read the lanes from the
  // GROUP summaries, the same source QPS Management uses.
  const acoSummariesQuery = usePerformanceSummaries(
    appMode ? practiceId : null,
    programYear,
    "GROUP",
    null,
    appMode,
  );

  const measureSource = useMemo<Array<{ measure_id: string; submission_method: string }> | undefined>(() => {
    if (!appMode) return selectionsQuery.data;
    if (!Array.isArray(acoSummariesQuery.data)) return undefined;
    const seen = new Set<string>();
    const rows: Array<{ measure_id: string; submission_method: string }> = [];
    for (const s of acoSummariesQuery.data) {
      if (!s?.measureId) continue;
      const method = s.submissionMethod || "MIPS CQM";
      const key = `${s.measureId}:${method}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({ measure_id: String(s.measureId), submission_method: method });
    }
    return rows;
  }, [appMode, selectionsQuery.data, acoSummariesQuery.data]);

  // Collection-type lanes present for this entity. MIPS CQM and eCQM share the
  // base measure id, so the lane must be chosen explicitly — a measure-only
  // filter would blend two lanes' outcomes for the same patient.
  const collectionTypes = useMemo(() => {
    const order = ["Medicare CQM", "MIPS CQM", "eCQM", "QCDR"];
    const set = new Set<string>((measureSource ?? []).map((s) => s.submission_method));
    return Array.from(set).sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    });
  }, [measureSource]);

  const collectionType = useMemo(() => {
    if (collectionTypes.length === 0) return null;
    return filters.collectionType && collectionTypes.includes(filters.collectionType)
      ? filters.collectionType
      : collectionTypes[0];
  }, [collectionTypes, filters.collectionType]);

  // Normalize a persisted lane the current entity does not have.
  useEffect(() => {
    if (!measureSource || collectionType === filters.collectionType) return;
    setFilters((f) => ({ ...f, collectionType }));
  }, [measureSource, collectionType, filters.collectionType]);

  /** Lane rows only (measure × the chosen collection type). */
  const laneSource = useMemo(
    () => (measureSource ?? []).filter((s) => !collectionType || s.submission_method === collectionType),
    [measureSource, collectionType],
  );

  const selectedMeasureIds = useMemo(() => {
    const ids = new Set<string>(laneSource.map((s) => s.measure_id));
    return Array.from(ids).sort();
  }, [laneSource]);

  // Persisted measure filter can carry ids from a different entity (MIPS
  // practice ↔ ACO) or a different lane. Drop ids the current lane does not
  // have; in APP Plus the set is small and fixed, so default to every lane
  // measure when nothing valid remains.
  useEffect(() => {
    if (!measureSource) return;
    const valid = new Set(selectedMeasureIds);
    setFilters((f) => {
      const kept = f.measureIds.filter((id) => valid.has(id));
      const next = kept.length === 0 && appMode ? selectedMeasureIds : kept;
      if (next.length === f.measureIds.length && next.every((id, i) => id === f.measureIds[i])) {
        return f;
      }
      return { ...f, measureIds: next };
    });
  }, [measureSource, selectedMeasureIds, appMode]);

  // -------------------------------------------------------------------------
  // TIN / clinician pickers
  //   APP Plus: TINs + clinicians (with roster names) from the TIN & NPI
  //             performance roll-up for the current lane.
  //   MIPS:     clinicians from the practice's outcome history (/providers).
  // -------------------------------------------------------------------------
  const tinNpiQuery = useAcoTinNpiPerformance(
    appMode ? (selectedAco?.id ?? null) : null,
    programYear,
    collectionType,
    appMode && !!collectionType,
  );
  const mipsProvidersQuery = useQuery({
    queryKey: ["performance", "providers", practiceId],
    queryFn: () => getProviders(),
    enabled: !!practiceId && !appMode,
    staleTime: 5 * 60_000,
  });

  const tinOptions = useMemo(() => {
    if (!appMode) return [];
    return (tinNpiQuery.data?.tins ?? [])
      .map((t) => ({
        value: t.tin,
        label: t.name ? `${t.name} (${t.tin})` : t.tin === "A0000" || t.tin === "UNKNOWN" ? "Unattributed TIN" : `TIN ${t.tin}`,
        patients: t.patients,
      }))
      .sort((a, b) => b.patients - a.patients);
  }, [appMode, tinNpiQuery.data]);

  const clinicianOptions = useMemo(() => {
    if (appMode) {
      return (tinNpiQuery.data?.npis ?? [])
        .filter((n) => n.npi !== "0000000000")
        .filter((n) => !filters.tin || n.tin === filters.tin)
        .map((n) => ({ value: n.npi, label: n.name ? `${n.name} (${n.npi})` : n.npi, patients: n.patients }))
        .sort((a, b) => b.patients - a.patients);
    }
    return (mipsProvidersQuery.data ?? [])
      .filter((p) => p.npi && p.npi !== "0000000000")
      .map((p) => ({ value: p.npi, label: p.displayName || (p.name ? `${p.name} (${p.npi})` : p.npi), patients: 0 }));
  }, [appMode, tinNpiQuery.data, mipsProvidersQuery.data, filters.tin]);

  // Drop a persisted TIN / clinician the current entity (or lane / TIN) does not have.
  useEffect(() => {
    const optsReady = appMode ? !!tinNpiQuery.data : !!mipsProvidersQuery.data;
    if (!optsReady) return;
    setFilters((f) => {
      const tinOk = !f.tin || (appMode && tinOptions.some((o) => o.value === f.tin));
      const npiOk = !f.npi || clinicianOptions.some((o) => o.value === f.npi);
      if (tinOk && npiOk) return f;
      return { ...f, tin: tinOk ? f.tin : null, npi: npiOk ? f.npi : null };
    });
  }, [appMode, tinNpiQuery.data, mipsProvidersQuery.data, tinOptions, clinicianOptions]);

  // Switching lanes: clear a By-measure focus that belongs to another lane
  // (its key carries the method, so it cannot appear in the new list).
  useEffect(() => {
    if (!focusMeasureKey || !collectionType) return;
    if (!focusMeasureKey.endsWith(`:${collectionType}`)) setFocusMeasureKey(null);
  }, [focusMeasureKey, collectionType]);

  /** Lane selections with method — By-measure list / Detail embed key. */
  const selectedMeasureRows = useMemo(() => {
    const rows = laneSource.map((s) => ({
      measureId: s.measure_id,
      submissionMethod: s.submission_method,
      key: `${s.measure_id}:${s.submission_method}`,
    }));
    rows.sort((a, b) =>
      a.measureId === b.measureId
        ? a.submissionMethod.localeCompare(b.submissionMethod)
        : a.measureId.localeCompare(b.measureId),
    );
    return rows;
  }, [laneSource]);

  // By-patient only: empty measureIds means "not chosen yet" (do not load all).
  const effectiveMeasureIds = filters.measureIds;

  const listFilterKey = useMemo(
    () =>
      JSON.stringify({
        m: effectiveMeasureIds,
        ct: collectionType,
        tin: filters.tin ?? null,
        npi: filters.npi ?? null,
        show: filters.show,
        g: filters.minGaps ?? 0,
        q: search,
        p: page,
      }),
    [effectiveMeasureIds, collectionType, filters.tin, filters.npi, filters.show, filters.minGaps, search, page],
  );

  const measuresChosen = filters.measureIds.length > 0;
  const listEnabled =
    !!practiceId && filters.view === "patient" && measuresChosen;
  const minGaps = filters.minGaps ?? 0;

  const listQuery = useQuery<PatientListResponse>({
    queryKey: qk.patientsList(practiceId, programYear, listFilterKey),
    queryFn: () => {
      const params = new URLSearchParams({
        year: String(programYear),
        page: String(page),
        pageSize: String(PAGE_SIZE),
        status: showScope.status,
        chase: showScope.chase,
        sort: "gaps_desc",
        measureIds: effectiveMeasureIds.join(","),
      });
      if (collectionType) params.set("submissionMethod", collectionType);
      if (filters.tin) params.set("tin", filters.tin);
      if (filters.npi) params.set("npi", filters.npi);
      if (search) params.set("search", search);
      if (minGaps > 0) params.set("minGaps", String(minGaps));
      return apiClient.get(`/api/patient-registry/${practiceId}/patients?${params.toString()}`);
    },
    enabled: listEnabled,
    placeholderData: keepPreviousSameEntity(2, practiceId),
  });

  const patients = listQuery.data?.patients ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Keep a valid selection: current selection if still listed, else first on page.
  useEffect(() => {
    if (filters.view !== "patient") return;
    if (patients.length === 0) return;
    if (selectedPatientId && patients.some((p) => p.patientId === selectedPatientId)) return;
    setSelectedPatientId(patients[0].patientId);
  }, [patients, selectedPatientId, filters.view]);

  // In-memory only (see PHI note above) — no URL write.
  const selectPatient = useCallback((patientId: string) => {
    setSelectedPatientId(patientId);
  }, []);

  const detailQuery = useQuery<PatientDetail>({
    queryKey: [...qk.patientDetail(practiceId, programYear, selectedPatientId ?? ""), collectionType ?? ""],
    queryFn: () =>
      apiClient.get(
        `/api/patient-registry/${practiceId}/patients/${encodeURIComponent(selectedPatientId!)}?year=${programYear}${
          collectionType ? `&submissionMethod=${encodeURIComponent(collectionType)}` : ""
        }`,
      ),
    enabled: !!practiceId && !!selectedPatientId && filters.view === "patient",
    placeholderData: keepPreviousSameEntity(2, practiceId),
    // Always revalidate so encounter codes are fresh (not a pre-encounters cache).
    staleTime: 0,
  });

  // Opening the demographics/codes modal forces a refetch — older cached payloads
  // omitted `encounters` and looked empty.
  useEffect(() => {
    if (!detailsOpen || !selectedPatientId || !practiceId) return;
    void detailQuery.refetch();
  }, [detailsOpen, selectedPatientId, practiceId]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const invalidateAfterChange = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["patients"] });
    if (selectedPatientId) {
      await queryClient.refetchQueries({
        queryKey: qk.patientDetail(practiceId, programYear, selectedPatientId),
      });
    }
    queryClient.invalidateQueries({ queryKey: ["performance", "summaries", practiceId] });
  }, [queryClient, practiceId, programYear, selectedPatientId]);

  const [savingOutcome, setSavingOutcome] = useState<string | null>(null);

  const recordOutcome = useCallback(
    async (m: WorksheetMeasure, outcome: RecordableOutcome | null) => {
      if (!selectedPatientId || isCurrentYearLocked) return;
      // null = clear numerator (Detail toggle-off → Needs review).
      const outcomeChanges =
        outcome === "met"
          ? { met: true, not: false, exc: false }
          : outcome === "not_met"
            ? { met: false, not: true, exc: false }
            : outcome === "exception"
              ? { met: false, not: false, exc: true }
              : { met: false, not: false, exc: false };

      const saveKey = `${m.measureId}:${m.submissionMethod}:${m.stratum ?? ""}`;
      setSavingOutcome(saveKey);
      try {
        // Same maintained path Detail.tsx uses (BulkOutcomeUpdater handles
        // versioning, is_active and conflict resolution server-side). The
        // single-edit PUT endpoint is NOT used anywhere and has a latent
        // varchar(30) change_type overflow — do not switch back to it.
        const result = await batchUpdatePatientOutcomes(
          practiceId,
          [
            {
              patientId: selectedPatientId,
              measureId: m.measureId,
              submissionMethod: m.submissionMethod,
              evaluationType: "GROUP",
              outcomeChanges,
              encounterId: m.encounterId ?? undefined,
              stratum: m.stratum ?? undefined,
            },
          ],
          programYear,
        );

        // Single-item batches run as a background job — poll briefly.
        if (result.jobId) {
          for (let i = 0; i < 30; i++) {
            await new Promise((r) => setTimeout(r, 500));
            const job = await checkJobStatus(result.jobId);
            if (job?.status === "completed") break;
            if (job?.status === "failed") {
              throw new Error(job?.error || "Outcome update failed");
            }
          }
        }

        toast({
          title: outcome ? "Outcome recorded" : "Outcome cleared",
          description: outcome
            ? `Measure ${m.measureId} set to ${STATUS_LABEL[outcome]}.`
            : `Measure ${m.measureId} returned to Needs review.`,
        });
        await invalidateAfterChange();
      } catch (e: any) {
        toast({
          title: "Failed to record outcome",
          description: e?.message ?? "",
          variant: "destructive",
        });
      } finally {
        setSavingOutcome(null);
      }
    },
    [selectedPatientId, isCurrentYearLocked, practiceId, programYear, toast, invalidateAfterChange],
  );

  const toggleComplete = useCallback(
    async (patientId: string, complete: boolean) => {
      if (isCurrentYearLocked) return;
      // Optimistic: flip the worksheet immediately
      queryClient.setQueryData<PatientDetail>(
        qk.patientDetail(practiceId, programYear, patientId),
        (prev) => (prev ? { ...prev, isComplete: complete } : prev),
      );
      // Optimistic list: Skip for now removes them from active Clinical result views;
      // under "Marked done", unchecking removes them. Icon updates otherwise.
      queryClient.setQueriesData<PatientListResponse>(
        { queryKey: ["patients", "list"] },
        (prev) => {
          if (!prev) return prev;
          if (
            (showScope.chase === "not_complete" && complete) ||
            (showScope.chase === "complete" && !complete)
          ) {
            return {
              ...prev,
              total: Math.max(0, prev.total - 1),
              patients: prev.patients.filter((p) => p.patientId !== patientId),
            };
          }
          return {
            ...prev,
            patients: prev.patients.map((p) =>
              p.patientId === patientId ? { ...p, isComplete: complete } : p,
            ),
          };
        },
      );
      try {
        await apiClient.put(
          `/api/patient-registry/${practiceId}/patients/${encodeURIComponent(patientId)}/complete?year=${programYear}`,
          { complete },
        );
        await queryClient.invalidateQueries({ queryKey: ["patients", "list"] });
      } catch (e: any) {
        queryClient.setQueryData<PatientDetail>(
          qk.patientDetail(practiceId, programYear, patientId),
          (prev) => (prev ? { ...prev, isComplete: !complete } : prev),
        );
        queryClient.invalidateQueries({ queryKey: ["patients", "list"] });
        toast({
          title: "Failed to update skip status",
          description: e?.message ?? "",
          variant: "destructive",
        });
      }
    },
    [isCurrentYearLocked, practiceId, programYear, queryClient, toast, showScope.chase],
  );

  /** Full filtered worklist CSV (same Show / measures / search / open-gaps). */
  const exportWorklist = useCallback(async () => {
    if (!practiceId || !measuresChosen || exportingWorklist) return;
    setExportingWorklist(true);
    try {
      const params = new URLSearchParams({
        year: String(programYear),
        status: showScope.status,
        chase: showScope.chase,
        sort: "name_asc",
        measureIds: effectiveMeasureIds.join(","),
      });
      if (collectionType) params.set("submissionMethod", collectionType);
      if (filters.tin) params.set("tin", filters.tin);
      if (filters.npi) params.set("npi", filters.npi);
      if (search) params.set("search", search);
      if (minGaps > 0) params.set("minGaps", String(minGaps));

      // Prefer the dedicated export endpoint; fall back to paging the list API
      // (same filters) if the route isn't live yet or returns 404.
      let csvText: string | null = null;
      let rowCount = 0;
      try {
        const res = await apiClient.request(
          `/api/patient-registry/${practiceId}/patients/export?${params.toString()}`,
        );
        csvText = await res.text();
        rowCount = Math.max(0, csvText.split(/\r?\n/).filter((l) => l.trim()).length - 1);
      } catch (e: any) {
        const msg = String(e?.message ?? "");
        const canFallback =
          /not found|404|failed to export/i.test(msg) || msg === "Not Found";
        if (!canFallback) throw e;

        const header = [
          "Row",
          "Patient ID",
          "Full Name",
          "First Name",
          "Last Name",
          "DOB",
          "Gender",
          "Open Gaps",
          "Gap Measure IDs",
          "Measure Count",
          "Providers (NPI)",
          "TIN",
          "Practice Name",
          "Skipped",
          "Program Year",
        ];
        const lines = [csvRow(header)];
        let pageNum = 1;
        let totalMatching = Infinity;
        const pageSize = 200;
        while ((pageNum - 1) * pageSize < totalMatching) {
          const pageParams = new URLSearchParams(params);
          pageParams.set("page", String(pageNum));
          pageParams.set("pageSize", String(pageSize));
          const data = (await apiClient.get(
            `/api/patient-registry/${practiceId}/patients?${pageParams.toString()}`,
          )) as PatientListResponse;
          totalMatching = data.total;
          for (const p of data.patients) {
            const providers = Array.isArray(p.providers) ? p.providers.join("; ") : "";
            const tins = Array.isArray((p as { tins?: string[] }).tins) ? (p as { tins?: string[] }).tins!.join("; ") : "";
            const practiceNames = Array.isArray((p as { practiceNames?: string[] }).practiceNames)
              ? (p as { practiceNames?: string[] }).practiceNames!.join("; ")
              : "";
            const gapIds = Array.isArray(p.gapMeasureIds)
              ? p.gapMeasureIds.join("; ")
              : "";
            lines.push(
              csvRow([
                lines.length, // Row (1-based; header is line 0)
                p.patientId,
                p.fullName,
                p.firstName,
                p.lastName,
                p.dob,
                p.gender,
                p.openGaps,
                gapIds,
                p.measureCount,
                providers,
                tins,
                practiceNames,
                p.isComplete ? "Yes" : "No",
                programYear,
              ]),
            );
          }
          if (data.patients.length === 0) break;
          pageNum += 1;
          if (pageNum > 500) break; // hard stop
        }
        csvText = "\uFEFF" + lines.join("\r\n");
        rowCount = lines.length - 1;
      }

      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `patients-worklist-${programYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast({
        title: "Worklist exported",
        description: `${rowCount.toLocaleString()} patients · sorted by name. Use the Row column to split batches (e.g. 1–500).`,
      });
    } catch (e: any) {
      toast({
        title: "Export failed",
        description: e?.message ?? "Could not build the worklist CSV.",
        variant: "destructive",
      });
    } finally {
      setExportingWorklist(false);
    }
  }, [
    practiceId,
    measuresChosen,
    exportingWorklist,
    programYear,
    showScope.status,
    showScope.chase,
    effectiveMeasureIds,
    collectionType,
    filters.tin,
    filters.npi,
    search,
    minGaps,
    toast,
  ]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const detail = detailQuery.data;
  // Worksheet: campaign measures ∩ Show filter. List membership is patient-level
  // (has ≥1 matching gap); cards on the right must match Show too — otherwise
  // "No result yet" still painted Exception/Met cards for the same patient.
  const worksheetMeasures = useMemo(() => {
    if (!detail) return [];
    const wanted =
      filters.measureIds.length > 0 ? new Set(filters.measureIds) : null;
    return detail.measures.filter((m) => {
      if (
        wanted &&
        !wanted.has(m.measureId) &&
        !wanted.has(baseMeasureId(m.measureId))
      ) {
        return false;
      }
      switch (filters.show) {
        case "no_result":
          return m.status === "incomplete";
        case "good_result":
          return measureIsGoodResult(m);
        case "bad_result":
          return measureIsBadResult(m);
        case "all":
        default:
          return true;
      }
    });
  }, [detail, filters.measureIds, filters.show]);

  // Collection-type picker, shared by both lenses. Always shown in APP Plus
  // (three lanes are possible); in MIPS only when the practice actually has
  // more than one lane, so single-method practices see no extra control.
  const collectionTypePicker =
    collectionTypes.length > 0 && (appMode || collectionTypes.length > 1) ? (
      <div className="flex items-center gap-1.5">
        <span className="hidden text-xs font-medium text-slate-500 sm:inline">Collection type</span>
        <Select
          value={collectionType ?? undefined}
          onValueChange={(v) => setFilters((f) => ({ ...f, collectionType: v }))}
        >
          <SelectTrigger className="h-9 w-[170px]" title="Outcomes are stored per collection type; MIPS CQM and eCQM share measure ids, so pick the lane to view.">
            <SelectValue placeholder="Collection type…" />
          </SelectTrigger>
          <SelectContent>
            {collectionTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;

  const ALL = "__all__";
  const scopePickers = (
    <>
      {appMode && (
        <div className="flex items-center gap-1.5">
          <span className="hidden text-xs font-medium text-slate-500 sm:inline">TIN</span>
          <Select
            value={filters.tin ?? ALL}
            onValueChange={(v) => setFilters((f) => ({ ...f, tin: v === ALL ? null : v, npi: null }))}
            disabled={tinOptions.length === 0}
          >
            <SelectTrigger className="h-9 w-[220px]" title="Participant TIN — patients with an encounter billed under this TIN">
              <SelectValue placeholder={tinNpiQuery.isPending ? "Loading…" : "All TINs"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All TINs ({tinOptions.length})</SelectItem>
              {tinOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label} <span className="text-muted-foreground">· {o.patients.toLocaleString()}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <span className="hidden text-xs font-medium text-slate-500 sm:inline">Clinician</span>
        <Select
          value={filters.npi ?? ALL}
          onValueChange={(v) => setFilters((f) => ({ ...f, npi: v === ALL ? null : v }))}
          disabled={clinicianOptions.length === 0}
        >
          <SelectTrigger className="h-9 w-[240px]" title="Patients seen by this clinician (any encounter in the year)">
            <SelectValue placeholder={(appMode ? tinNpiQuery.isPending : mipsProvidersQuery.isPending) ? "Loading…" : "All clinicians"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All clinicians ({clinicianOptions.length})</SelectItem>
            {clinicianOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}{o.patients > 0 ? <span className="text-muted-foreground"> · {o.patients.toLocaleString()}</span> : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  if (!practiceId) {
    return (
      <main className="container mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-2xl font-semibold tracking-tight">Patients</h1>
        <p className="mt-2 text-muted-foreground">
          {appMode
            ? "Select an ACO to view its patients."
            : "Select a practice to view its patients."}
        </p>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>Patients | Gap Chase Worklist</title>
        <meta name="description" content="Patient-centric quality gap worklist." />
      </Helmet>

      {/* Print: only the open worksheet, portrait. Screen design unchanged. */}
      <style>{`
        @media print {
          @page { size: portrait; margin: 0.5in; }
          body { background: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body * { visibility: hidden !important; }
          #patients-print-scope, #patients-print-scope * { visibility: visible !important; }
          #patients-print-scope {
            position: absolute !important;
            inset: 0 auto auto 0 !important;
            width: 100% !important;
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          .patients-print-hide { display: none !important; }
          .patients-print-card { break-inside: avoid; }
          .patients-print-checkbox {
            display: inline-block !important;
            width: 12px; height: 12px;
            border: 1.5px solid #0f172a; border-radius: 2px;
            vertical-align: middle;
          }
        }
        .patients-print-checkbox { display: none; }
      `}</style>

      <main className="mx-auto w-full max-w-none space-y-4 px-4 py-6">
        <div className="patients-print-hide flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Patients</h1>
          <div className="flex items-center gap-1 rounded-lg border bg-white p-1">
            <Button
              variant={filters.view === "patient" ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilters((f) => ({ ...f, view: "patient" }))}
            >
              <Users className="mr-1.5 h-4 w-4" /> By patient
            </Button>
            <Button
              variant={filters.view === "measure" ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilters((f) => ({ ...f, view: "measure" }))}
            >
              <ClipboardList className="mr-1.5 h-4 w-4" /> By measure
            </Button>
          </div>
        </div>

        <div className="patients-print-hide">
          <YearLockBanner />
        </div>

        {/* Filter bar — By patient only */}
        {filters.view === "patient" && (
          <div className="patients-print-hide flex flex-wrap items-center gap-2">
            {collectionTypePicker}
            {scopePickers}
            <MeasureMultiSelect
              measureIds={selectedMeasureIds}
              selectedIds={filters.measureIds}
              measureTitle={measureTitle}
              onChange={(ids) => setFilters((f) => ({ ...f, measureIds: ids }))}
              disabled={!measureSource || selectedMeasureIds.length === 0}
            />

            <div className="flex items-center gap-1.5">
              <span className="hidden text-xs font-medium text-slate-500 sm:inline">
                Clinical result
              </span>
              <Select
                value={filters.show}
                onValueChange={(v: ShowPreset) => setFilters((f) => ({ ...f, show: v }))}
                disabled={!measuresChosen}
              >
                <SelectTrigger
                  className="h-9 w-[180px]"
                  title={SHOW_PRESETS[filters.show]?.tooltip}
                >
                  <SelectValue placeholder="Clinical result…" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SHOW_PRESETS) as ShowPreset[]).map((key) => (
                    <SelectItem key={key} value={key} title={SHOW_PRESETS[key].tooltip}>
                      {SHOW_PRESETS[key].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {filters.view === "patient" ? (
          <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
            {/* Patient worklist — search lives here with the list */}
            <Card className="patients-print-hide overflow-hidden border bg-white shadow-sm">
              <div className="space-y-2.5 border-b px-3 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Worklist</div>
                    <div className="text-xs text-muted-foreground">
                      {listQuery.isError
                        ? "Couldn't load"
                        : listQuery.data
                          ? `${total.toLocaleString()} patient${total === 1 ? "" : "s"}`
                          : measuresChosen
                            ? "Loading…"
                            : "Select measures"}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      disabled={!measuresChosen || !listQuery.data || total === 0 || exportingWorklist}
                      title="Export the full filtered list (CSV) for coder assignment"
                      onClick={() => exportWorklist()}
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span className="ml-1 hidden sm:inline">
                        {exportingWorklist ? "…" : "Export"}
                      </span>
                    </Button>
                    <WorklistPager
                      page={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      disabled={!listQuery.data}
                    />
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Name, DOB, or ID…"
                    className="h-9 w-full pl-8 text-sm"
                    disabled={!measuresChosen}
                  />
                </div>
                {measuresChosen && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-medium text-slate-500">
                      Open gaps
                    </span>
                    {MIN_GAPS_OPTIONS.map((opt) => {
                      const active = minGaps === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          title={
                            opt.value === 0
                              ? "No minimum — include every patient in Show"
                              : `Only patients with ${opt.value} or more open gaps`
                          }
                          onClick={() =>
                            setFilters((f) => ({ ...f, minGaps: opt.value }))
                          }
                          className={`rounded px-2 py-0.5 text-xs font-semibold transition-colors ${
                            active
                              ? "bg-slate-800 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <CardContent className="p-0">
                {!measuresChosen ? (
                  <div className="p-6 text-sm text-slate-500">
                    Select one or more measures above to load the patient list.
                  </div>
                ) : listQuery.isError ? (
                  <QueryError
                    message={(listQuery.error as Error)?.message}
                    onRetry={() => listQuery.refetch()}
                  />
                ) : !listQuery.data ? (
                  <ListSkeleton />
                ) : patients.length === 0 ? (
                  <div className="space-y-2 p-6 text-sm text-slate-500">
                    <p className="font-medium text-slate-800">No patients to show.</p>
                    {search ? (
                      <p>Nothing matches that search. Clear it or try another name / ID.</p>
                    ) : minGaps > 0 ? (
                      <p>
                        Nobody has {minGaps}+ open gaps. Try{" "}
                        <span className="font-medium">All</span> or a lower floor.
                      </p>
                    ) : filters.show === "no_result" ? (
                      <p>
                        No one is waiting for a result on the selected measures — either
                        this practice has no uploaded patients for {programYear}, or
                        everyone already has a result. Try{" "}
                        <span className="font-medium">All statuses</span>.
                      </p>
                    ) : filters.show === "good_result" ? (
                      <p>No patients with a good clinical result on the selected measures.</p>
                    ) : filters.show === "bad_result" ? (
                      <p>No patients with a bad clinical result on the selected measures.</p>
                    ) : (
                      <p>
                        No patients for this practice in {programYear} yet. Upload claims
                        and wait for calculation to finish, then refresh.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {patients.map((p) => {
                      const active = p.patientId === selectedPatientId;
                      return (
                        <button
                          key={p.patientId}
                          type="button"
                          onClick={() => selectPatient(p.patientId)}
                          className={`block w-full border-l-[3px] px-4 py-3.5 text-left transition-colors ${
                            active
                              ? "border-l-blue-700 bg-blue-50"
                              : "border-l-transparent hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <span
                                role="link"
                                tabIndex={0}
                                className="block truncate text-[15px] font-semibold leading-snug text-blue-700 underline-offset-2 hover:text-blue-800 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectPatient(p.patientId);
                                  setDetailsOpen(true);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    selectPatient(p.patientId);
                                    setDetailsOpen(true);
                                  }
                                }}
                              >
                                {p.fullName}
                              </span>
                              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-slate-500">
                                <span>DOB {formatDate(p.dob)}</span>
                                {p.isComplete && (
                                  <span className="font-medium text-slate-500">
                                    · Skipped
                                  </span>
                                )}
                              </div>
                            </div>
                            {p.openGaps > 0 ? (
                              <span className="inline-flex min-w-[1.75rem] shrink-0 items-center justify-center rounded bg-red-500 px-2 py-1 text-xs font-bold text-white">
                                {p.openGaps}
                              </span>
                            ) : (
                              <span className="inline-flex min-w-[1.75rem] shrink-0 items-center justify-center rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-600">
                                0
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {listQuery.data && totalPages > 1 && (
                  <div className="border-t px-3 py-2">
                    <WorklistPager
                      page={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Worksheet */}
            <div id="patients-print-scope">
              <Card>
                <CardContent className="p-5">
                  {!measuresChosen ? (
                    <div className="py-16 text-center text-sm text-muted-foreground">
                      Select measures in the dropdown to load patients and open worksheets.
                    </div>
                  ) : !selectedPatientId ? (
                    <div className="py-16 text-center text-sm text-muted-foreground">
                      Select a patient from the list.
                    </div>
                  ) : detailQuery.isError ? (
                    <QueryError
                      message={(detailQuery.error as Error)?.message}
                      onRetry={() => detailQuery.refetch()}
                    />
                  ) : !detail ? (
                    <div className="space-y-3">
                      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
                      <div className="h-4 w-80 animate-pulse rounded bg-muted" />
                      <div className="h-32 animate-pulse rounded bg-muted" />
                      <div className="h-32 animate-pulse rounded bg-muted" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Header — Print primary; Skip is rare/quiet */}
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="patients-print-hide text-left text-xl font-bold text-blue-700 underline-offset-2 hover:text-blue-800 hover:underline"
                            onClick={() => setDetailsOpen(true)}
                          >
                            {detail.fullName}
                          </button>
                          <h2 className="hidden text-xl font-bold print:block">{detail.fullName}</h2>
                          <p className="mt-0.5 text-sm text-slate-600">
                            {detail.gender ?? "—"} · DOB {formatDate(detail.dob)} · ID{" "}
                            {detail.patientId}
                          </p>
                          {detail.providers.length > 0 && (
                            <p className="mt-0.5 text-xs text-slate-500">
                              Providers: {detail.providers.join(", ")}
                            </p>
                          )}
                          {detail.isComplete && (
                            <p className="patients-print-hide mt-1 text-xs font-medium text-slate-500">
                              Skipped for now
                            </p>
                          )}
                          <p className="hidden text-xs text-muted-foreground print:block">
                            Program year {programYear} · printed{" "}
                            {new Date().toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <button
                            type="button"
                            disabled={isCurrentYearLocked}
                            title={
                              detail.isComplete
                                ? "Return this patient to Clinical result filters"
                                : "Park this patient — hide from No result / Good / Bad until you undo"
                            }
                            onClick={() =>
                              toggleComplete(detail.patientId, !detail.isComplete)
                            }
                            className="patients-print-hide text-xs text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:opacity-50"
                          >
                            {detail.isComplete ? "Undo skip" : "Skip for now"}
                          </button>
                          <span className="hidden items-center gap-1.5 text-sm print:flex">
                            <span className="patients-print-checkbox" /> Skipped
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="patients-print-hide h-9"
                            onClick={() => window.print()}
                          >
                            <Printer className="mr-1.5 h-4 w-4" /> Print
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {worksheetMeasures.map((m) => {
                          const saveKey = `${m.measureId}:${m.submissionMethod}:${m.stratum ?? ""}`;
                          return (
                            <WorksheetMeasureCard
                              key={`${detail.patientId}:${saveKey}`}
                              m={m}
                              title={measureTitle(m.measureId)}
                              questions={getMeasureQuestions(baseMeasureId(m.measureId))}
                              saving={savingOutcome === saveKey}
                              locked={isCurrentYearLocked}
                              onRecord={recordOutcome}
                            />
                          );
                        })}
                        {worksheetMeasures.length === 0 && (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            No measures match{" "}
                            <span className="font-medium">
                              {SHOW_PRESETS[filters.show].label}
                            </span>{" "}
                            for this patient. Try{" "}
                            <span className="font-medium">All statuses</span>.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* ------------------------------------------------------------- */
          /* By measure — full-width Gap Management Detail + measure pick  */
          /* ------------------------------------------------------------- */
          <div className="space-y-4">
            <div className="patients-print-hide flex flex-wrap items-center gap-3">
              {collectionTypePicker}
              <span className="text-sm text-muted-foreground">Measure</span>
              <Select
                value={focusMeasureKey ?? undefined}
                onValueChange={(v) => setFocusMeasureKey(v)}
              >
                <SelectTrigger className="h-9 w-[min(100%,420px)]">
                  <SelectValue placeholder="Select a measure…" />
                </SelectTrigger>
                <SelectContent>
                  {selectedMeasureRows.map((row) => (
                    <SelectItem key={row.key} value={row.key}>
                      <span className="font-mono font-semibold">{row.measureId}</span>
                      <span className="text-muted-foreground">
                        {" "}
                        · {row.submissionMethod}
                        {measureTitle(row.measureId)
                          ? ` — ${measureTitle(row.measureId).slice(0, 40)}`
                          : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!focusMeasureKey ? (
              <Card>
                <CardContent className="p-10 text-center text-sm text-muted-foreground">
                  Select a measure to open the same Detailed Results view as Gap Management.
                </CardContent>
              </Card>
            ) : (
              <MeasureDetail
                key={focusMeasureKey}
                measureIdFromUrl={focusMeasureKey}
                embedded
                practiceIdOverride={appMode ? practiceId : undefined}
              />
            )}
          </div>
        )}
      </main>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="flex max-h-[88vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
          <DialogHeader className="shrink-0 space-y-1 border-b border-slate-100 px-6 pb-4 pt-6 pr-12 text-left">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <DialogTitle className="text-xl font-semibold tracking-tight text-blue-700">
                  {detail?.fullName ?? "Patient details"}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
                    {detail ? (
                      <>
                        <span className="font-mono text-slate-600">{detail.patientId}</span>
                        <span className="text-slate-300" aria-hidden>
                          ·
                        </span>
                        <span>
                          {detail.gender ?? "—"} · DOB {formatDate(detail.dob)}
                        </span>
                        <span className="text-slate-300" aria-hidden>
                          ·
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          PY {programYear}
                        </span>
                      </>
                    ) : (
                      <span>Loading patient…</span>
                    )}
                  </div>
                </DialogDescription>
              </div>
              {detail && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => downloadPatientCsv(detail, programYear)}
                >
                  <Download className="mr-1.5 h-4 w-4" />
                  Export CSV
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {!detail && detailsOpen ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
                  ))}
                </div>
                <div className="h-40 animate-pulse rounded-lg bg-slate-100" />
              </div>
            ) : detail ? (
              <div className="space-y-6">
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {(
                    [
                      { label: "Patient ID", value: detail.patientId, mono: true },
                      { label: "Date of birth", value: formatDate(detail.dob) },
                      { label: "Gender", value: detail.gender ?? "—" },
                      {
                        label: "Providers",
                        value:
                          detail.providers.length > 0
                            ? `${detail.providers.length} NPI${detail.providers.length === 1 ? "" : "s"}`
                            : "—",
                      },
                    ] as const
                  ).map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-sm"
                    >
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                        {item.label}
                      </dt>
                      <dd
                        className={`mt-1 truncate text-sm font-semibold text-slate-800 ${
                          "mono" in item && item.mono ? "font-mono text-[13px]" : ""
                        }`}
                        title={String(item.value)}
                      >
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>

                {detail.providers.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Providers (NPI)
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.providers.map((npi) => (
                        <span
                          key={npi}
                          className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-700"
                        >
                          {npi}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-800">
                      Encounters &amp; codes
                    </h3>
                    {(detail.encounters?.length ?? 0) > 0 && (
                      <span className="text-xs text-slate-500">
                        {detail.encounters!.length} visit
                        {detail.encounters!.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  {detailQuery.isFetching && (detail.encounters?.length ?? 0) === 0 ? (
                    <div className="space-y-2">
                      <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                      <div className="h-28 animate-pulse rounded-lg bg-slate-100" />
                    </div>
                  ) : (detail.encounters?.length ?? 0) === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No claim encounters on file for this patient in {programYear}.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                      <div className="max-h-[min(48vh,420px)] overflow-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="whitespace-nowrap px-3 py-2.5 font-semibold">
                                Date of Service
                              </th>
                              <th className="px-3 py-2.5 font-semibold">CPT</th>
                              <th className="px-3 py-2.5 font-semibold">Modifiers</th>
                              <th className="px-3 py-2.5 font-semibold">ICD-10</th>
                              <th className="whitespace-nowrap px-3 py-2.5 font-semibold">NPI</th>
                              <th className="whitespace-nowrap px-3 py-2.5 font-semibold">POS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(detail.encounters ?? []).map((enc, i) => (
                              <tr
                                key={`${enc.encounterId ?? "e"}-${i}`}
                                className="bg-white hover:bg-blue-50/40"
                              >
                                <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-800">
                                  {formatDate(enc.dos)}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <CodeChips codes={splitCodes(enc.cptCodes)} tone="blue" />
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <CodeChips codes={splitCodes(enc.cptModifiers)} />
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <CodeChips codes={splitCodes(enc.icd10Codes)} tone="amber" />
                                </td>
                                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-slate-600">
                                  {enc.npi || "—"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-600">
                                  {enc.placeOfService || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
